import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ClientSites from "@/components/ClientSites";
import ClientUsersModal from "@/components/ClientUsersModal";
import PendingInvites from "@/components/PendingInvites";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Building2, Users } from "lucide-react";

const empty = { name: "", industry: "", contact_person: "", contact_email: "", contact_phone: "", site_address: "", is_residential: false };

export default function Clients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = isAdmin || (user?.role === "staff" && user?.capabilities?.includes("clients.manage"));
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [manageFor, setManageFor] = useState(null); // { id, name } | null
  const [invitesKey, setInvitesKey] = useState(0);
  const bumpInvites = () => setInvitesKey((k) => k + 1);

  const load = () => api.get("/clients").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/clients/${editingId}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client added");
      }
      setForm(empty); setEditingId(null); setShowForm(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const edit = (c) => { setForm(c); setEditingId(c.id); setShowForm(true); };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/clients/${c.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Directory"
        title="Clients"
        subtitle="Manufacturing partners served by HBT Waste."
        actions={canManage && (
          <button onClick={() => { setShowForm(true); setEditingId(null); setForm(empty); }} className="btn-primary inline-flex items-center gap-2" data-testid="add-client-btn">
            <Plus className="w-4 h-4" /> Add client
          </button>
        )}
      />
      <div className="px-8 lg:px-12 py-8">
        {isAdmin && <PendingInvites key={invitesKey} onChange={bumpInvites} />}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {items.map((c) => (
            <div key={c.id} className="hbt-card p-6" data-testid={`client-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-md grid place-items-center" style={{ background: "rgba(26,54,45,0.08)", color: "var(--forest)" }}>
                  <Building2 className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <span className="pill forest">{c.industry || "Manufacturing"}</span>
              </div>
              <div className="mt-5">
                <div className="font-display text-lg font-semibold tracking-tight">{c.name}</div>
                <div className="text-xs text-neutral-500 mt-1">{c.site_address || "—"}</div>
              </div>
              <div className="mt-5 space-y-1 text-xs text-neutral-600">
                {c.contact_person && <div><span className="text-neutral-400">Contact · </span>{c.contact_person}</div>}
                {c.contact_email && <div><span className="text-neutral-400">Email · </span>{c.contact_email}</div>}
                {c.contact_phone && <div><span className="text-neutral-400">Phone · </span>{c.contact_phone}</div>}
              </div>
              {canManage && (
                <div className="mt-5 flex items-center gap-2 flex-wrap">
                  {isAdmin && (
                    <button onClick={() => setManageFor({ id: c.id, name: c.name })} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid={`manage-users-${c.id}`}>
                      <Users className="w-3.5 h-3.5" /> Manage users
                    </button>
                  )}
                  <button onClick={() => edit(c)} className="btn-outline text-xs" data-testid={`edit-client-${c.id}`}>Edit</button>
                  {isAdmin && <button onClick={() => remove(c)} className="btn-outline text-xs" data-testid={`delete-client-${c.id}`}>Delete</button>}
                </div>
              )}
              <ClientSites clientId={c.id} canEdit={canManage} />
            </div>
          ))}
        </div>

        {items.length === 0 && (
          <div className="hbt-card p-16 text-center text-neutral-500">No clients yet.</div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="hbt-card p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="client-form-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="hbt-label">{editingId ? "Edit" : "New"}</div>
                <div className="font-display text-2xl font-semibold mt-1">{editingId ? "Update client" : "Add a client"}</div>
              </div>
              <button onClick={() => setShowForm(false)} className="text-neutral-400 hover:text-black" data-testid="close-client-form"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="hbt-label block mb-2">Company name</label>
                <input required className="hbt-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Industry</label>
                  <input className="hbt-input" value={form.industry || ""} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="client-industry-input" />
                </div>
                <div>
                  <label className="hbt-label block mb-2">Site address</label>
                  <input className="hbt-input" value={form.site_address || ""} onChange={(e) => setForm({ ...form, site_address: e.target.value })} data-testid="client-address-input" />
                </div>
                <label className="flex items-center gap-2 text-sm text-neutral-600 col-span-2">
                  <input type="checkbox" checked={!!form.is_residential} onChange={(e) => setForm({ ...form, is_residential: e.target.checked })} data-testid="client-residential-check" />
                  Residential client (eligible for a resident recycling guide)
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Contact person</label>
                  <input className="hbt-input" value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="client-contact-input" />
                </div>
                <div>
                  <label className="hbt-label block mb-2">Phone</label>
                  <input className="hbt-input" value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="client-phone-input" />
                </div>
              </div>
              <div>
                <label className="hbt-label block mb-2">Email</label>
                <input type="email" className="hbt-input" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="client-email-input" />
              </div>
              <div className="flex justify-end gap-2 pt-3">
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline" data-testid="cancel-client">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="save-client">{editingId ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ClientUsersModal
        open={!!manageFor}
        clientId={manageFor?.id}
        clientName={manageFor?.name}
        currentUserId={user?.id}
        onClose={() => { setManageFor(null); bumpInvites(); }}
      />
    </div>
  );
}
